import {assert} from 'Asserts';
import {ProcedureMethodExecutionInstanceStatus} from 'graphql-client/__generated/graphqlGlobalTypes';

import {MethodExecutionStatus} from '../design-state/getStudyDesignFromProcedureRun';

export {TERMINAL_PROCESS_TASK_STATUS_TYPES, resolveTasklessStatuses} from './taskStatusUtils';
export type {PredecessorEdge} from './taskStatusUtils';

const NOT_STARTED_PMEI_STATUSES = new Set<ProcedureMethodExecutionInstanceStatus>([
  ProcedureMethodExecutionInstanceStatus.PLANNED,
  ProcedureMethodExecutionInstanceStatus.PENDING,
]);

const TERMINAL_PMEI_STATUSES = new Set<ProcedureMethodExecutionInstanceStatus>([
  ProcedureMethodExecutionInstanceStatus.COMPLETED,
  ProcedureMethodExecutionInstanceStatus.SKIPPED,
  ProcedureMethodExecutionInstanceStatus.FAILED,
]);

/**
 * Terminal PMEI statuses from which a run can advance to the next unit op. Once every PMEI of a method
 * is terminal, the next method becomes active only if at least one of them has one of these statuses.
 */
const ADVANCING_PMEI_STATUSES = new Set<ProcedureMethodExecutionInstanceStatus>([
  ProcedureMethodExecutionInstanceStatus.COMPLETED,
  ProcedureMethodExecutionInstanceStatus.SKIPPED,
]);

export interface MethodIdAndName {
  id: string;
  name: string;
}

function areAllStatusesNotStarted(statuses: Array<ProcedureMethodExecutionInstanceStatus | null>): boolean {
  return statuses.every((status) => status == null || NOT_STARTED_PMEI_STATUSES.has(status));
}

function canAdvanceFromPreviousMethod(
  statuses: Array<ProcedureMethodExecutionInstanceStatus | null>
): boolean {
  if (statuses.length === 0) {
    return false;
  }

  const allTerminal = statuses.every((status) => status != null && TERMINAL_PMEI_STATUSES.has(status));
  const hasAdvancingStatus = statuses.some((status) => status != null && ADVANCING_PMEI_STATUSES.has(status));
  return allTerminal && hasAdvancingStatus;
}

/**
 * Returns the active method by scanning methods in flowchart order.
 *
 * A method is active when all of its PMEI statuses are missing (`null`), `PLANNED`, or `PENDING`,
 * and if it is not the first method, the immediately previous method's PMEI statuses are all
 * terminal (`COMPLETED`, `SKIPPED`, or `FAILED`) with at least one advancing status (`COMPLETED`
 * or `SKIPPED`).
 */
export function getActiveMethod(
  methods: Array<MethodIdAndName>,
  methodExecutionStatuses: Array<MethodExecutionStatus>
): MethodIdAndName | null {
  const statusesByMethodId = methodExecutionStatuses.reduce((statusesById, instance) => {
    statusesById.set(instance.methodId, [
      ...(statusesById.get(instance.methodId) ?? []),
      instance.pmeiStatus,
    ]);
    return statusesById;
  }, new Map<string, Array<ProcedureMethodExecutionInstanceStatus | null>>());

  for (let methodIndex = 0; methodIndex < methods.length; methodIndex++) {
    const method = methods[methodIndex];
    const methodStatuses = statusesByMethodId.get(method.id) ?? [];
    if (!areAllStatusesNotStarted(methodStatuses)) {
      continue;
    }

    if (methodIndex === 0) {
      return method;
    }

    const previousMethod = methods[methodIndex - 1];
    const previousMethodStatuses = statusesByMethodId.get(previousMethod.id) ?? [];
    if (canAdvanceFromPreviousMethod(previousMethodStatuses)) {
      return method;
    }
  }

  // No active method
  return null;
}

/**
 * Returns the active method id and every downstream method id in flowchart order.
 */
export function getActiveAndDownstreamMethodIds(
  methods: Array<MethodIdAndName>,
  activeMethodId: string
): ReadonlySet<string> {
  const activeMethodIndex = methods.findIndex((method) => method.id === activeMethodId);
  assert(activeMethodIndex >= 0, `Active method ${activeMethodId} is not among the provided methods`);

  return new Set(methods.slice(activeMethodIndex).map((method) => method.id));
}
